/**
 * STEP GROWTH-B3-FIX-2 · LIVE BOOST wizard — Campaign flow aligned.
 *
 * Concept:
 *   LIVEMOA LIVE BOOST supports BOTH:
 *     A) 현재 LIVE 부스팅 (live-now)  · campaign_type=live_boost
 *     B) 예정 LIVE 부스팅 (upcoming)  · campaign_type=live_boost
 *   Same engine · same ad_campaigns row · admin/attribution unchanged.
 *
 * REUSE ONLY (matches FAN BOOST / prior B3 · no new engine):
 *   · live_schedules picker           → /api/seller/growth/live-boost/broadcasts/pick
 *   · streaming_sessions (destination) → resolved server-side in landing endpoint
 *   · Existing short_clips            → /api/seller/short-clips
 *   · Existing video pipeline         → /api/seller/short-clip-media
 *   · Existing image pipeline         → /api/uploads
 *   · Existing wallet                 → /api/seller/wallet
 *   · CreativePreview                 → shared from FanBoostSection.js
 *   · Broadcast registration          → /seller/dashboard?section=live (deep link)
 *
 * INTERNAL REAL only: no Meta/TikTok/Google Ads calls · no wallet deduction.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  X, ChevronLeft, ChevronRight, Radio, Video, Youtube, Instagram, Music,
  Film, Upload as UploadIcon, Check, ExternalLink, Copy, Wallet, Loader2,
  AlertCircle, Calendar, Sparkles, MonitorPlay, ArrowRight, Info,
} from 'lucide-react';
import api from '@/lib/api';
import { CreativePreview } from '@/components/seller/FanBoostSection';

// ─── constants ─────────────────────────────────────────────────────────────
const PLATFORMS = [
  { value: 'instagram', label: 'Instagram', Icon: Instagram },
  { value: 'tiktok',    label: 'TikTok',    Icon: Music },
  { value: 'youtube',   label: 'YouTube',   Icon: Youtube },
];
const CREATIVE_MODES = [
  {
    value: 'live_direct',
    label: 'LIVE 바로 홍보',
    headline: '현재/예정 LIVE 방송을 그대로 홍보하여 시청자를 유입합니다.',
    hint: '별도의 광고 소재 없이 바로 진행할 수 있습니다.',
    Icon: MonitorPlay,
  },
  {
    value: 'video_to_live',
    label: '영상으로 LIVE 홍보',
    headline: '기존 숏폼이나 광고 영상을 활용해 LIVE 방송으로 시청자를 유입합니다.',
    hint: '숏폼 선택 · 광고 소재 업로드 옵션이 열립니다.',
    Icon: Film,
  },
];
const NATIONWIDE = '전국';
const LOCAL_REGIONS = ['서울','경기','인천','부산','대구','광주','대전','울산','세종','강원','충북','충남','전북','전남','경북','경남','제주'];
const REGIONS = [NATIONWIDE, ...LOCAL_REGIONS];
const CATEGORIES = ['패션','뷰티','푸드','리빙','테크','유아동','반려동물','취미','스포츠'];
const GENDERS = [{ v: 'any', l: '전체' }, { v: 'female', l: '여성' }, { v: 'male', l: '남성' }];
const PLATFORM_FILTERS = [
  { value: 'all',       label: '전체' },
  { value: 'youtube',   label: 'YouTube' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok',    label: 'TikTok' },
];
// STEP GROWTH-B3-FIX-2 · canonical internal timing modes.  These are LIVEMOA
// canonical settings; the future Ads adapter will map each to whatever the
// external platform supports.  DO NOT assume Meta/TikTok/YouTube parity.
const TIMING_LIVE_NOW = [
  { value: 'until_end', label: '방송 종료까지', hint: 'LIVE 종료 시 자동 종료' },
  { value: '1h',        label: '1시간',        hint: '시작 후 1시간' },
  { value: '2h',        label: '2시간',        hint: '시작 후 2시간' },
  { value: 'custom',    label: '직접 설정',      hint: '아래 시작·종료 시각으로 지정' },
];
const TIMING_UPCOMING = [
  { value: 'before_start', label: '방송 전부터 홍보', hint: '방송 시작 전까지 노출' },
  { value: 'at_start',     label: '방송 시작 시',     hint: 'LIVE 시작 시점부터 노출' },
  { value: 'custom',       label: '직접 설정',        hint: '아래 시작·종료 시각으로 지정' },
];
const LBC_DEFAULTS = {
  promo_message: '지금 라이브 진행 중! 특가 상품을 놓치지 마세요.',
  cta_label:     '라이브 시청하기',
};
const STATUS_LABEL = {
  draft: '초안', pending: '승인대기', approved: '승인', running: '진행중',
  rejected: '반려', paused: '일시정지', ended: '종료',
};
const STATUS_TONE = {
  draft: 'bg-gray-500/10 text-gray-500',
  pending: 'bg-amber-500/10 text-amber-600',
  approved: 'bg-emerald-500/10 text-emerald-600',
  running: 'bg-emerald-500/10 text-emerald-600',
  rejected: 'bg-red-500/10 text-red-600',
  paused: 'bg-purple-500/10 text-purple-600',
  ended: 'bg-black/10 text-gray-600',
};

const won = (n) => (n == null ? '-' : new Intl.NumberFormat('ko-KR').format(Number(n) || 0) + '원');
const fmtDate = (d) => (d ? new Date(d).toLocaleString('ko-KR') : '-');
const fmtDur  = (s) => (s == null ? '' : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`);
const buildLandingUrl = (id) => `${window.location.origin}/gl/${id}`;

// Compute canonical end_at given start_at + timing hint (client preview only —
// backend is the SoT · this only pre-fills the datetime input for the seller).
const computeSuggestedEnd = (start, mode) => {
  if (!start) return null;
  const s = new Date(start).getTime();
  if (Number.isNaN(s)) return null;
  const hours = { '1h': 1, '2h': 2, 'until_end': 4 }[mode] ?? 24;
  return new Date(s + hours * 3_600_000);
};

// ═════════════════════════════════════════════════════════════════════════
// Wizard modal
// ═════════════════════════════════════════════════════════════════════════
function LiveBoostWizard({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);

  // STEP 2 · broadcast selection
  const [broadcasts, setBroadcasts] = useState({ live_now: [], upcoming: [] });
  const [bcastLoading, setBcastLoading] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [broadcastUrl, setBroadcastUrl] = useState('');

  // STEP 3 · creative mode
  const [creativeMode, setCreativeMode] = useState('live_direct');
  const [creativeKind, setCreativeKind] = useState('short_clip');
  const [shortClips, setShortClips]     = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [selectedClip, setSelectedClip] = useState(null);
  const [clipPlatformFilter, setClipPlatformFilter] = useState('all');
  const [uploaded, setUploaded]         = useState(null);
  const [uploading, setUploading]       = useState(false);
  const fileRef = useRef(null);

  // STEP 4 · ad platform
  const [platform, setPlatform] = useState('instagram');

  // STEP 5 · targeting
  const [targeting, setTargeting] = useState({
    regions: [], age_min: 20, age_max: 45, genders: ['any'], categories: [],
  });

  // STEP 6 · budget + timing
  const [timingMode, setTimingMode] = useState('until_end');
  const [budget, setBudget]   = useState(100000);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt]     = useState('');
  const [title, setTitle]     = useState('');

  const [saving, setSaving] = useState(false);
  const [wallet, setWallet] = useState(null);

  // Canonical LIVE BOOST config (promo_message + CTA + creative_mode + timing).
  const [lbc, setLbc] = useState({ ...LBC_DEFAULTS });

  // Reset on open ── every state field lives here so we can safely re-open.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setBroadcasts({ live_now: [], upcoming: [] }); setSelectedBroadcast(null);
    setAdvancedOpen(false); setBroadcastUrl('');
    setCreativeMode('live_direct'); setCreativeKind('short_clip');
    setShortClips([]); setSelectedClip(null); setClipPlatformFilter('all');
    setUploaded(null); setUploading(false);
    setPlatform('instagram');
    setTargeting({ regions: [], age_min: 20, age_max: 45, genders: ['any'], categories: [] });
    setTimingMode('until_end'); setBudget(100000); setStartAt(''); setEndAt('');
    setTitle(''); setSaving(false);
    setLbc({ ...LBC_DEFAULTS });
  }, [open]);

  // STEP 2 · load broadcasts on entry (READ-ONLY over live_schedules).
  useEffect(() => {
    if (!open || step !== 2) return;
    (async () => {
      setBcastLoading(true);
      try {
        const r = await api.get('/seller/growth/live-boost/broadcasts/pick');
        setBroadcasts({
          live_now: r.data?.live_now || [],
          upcoming: r.data?.upcoming || [],
        });
      } catch { setBroadcasts({ live_now: [], upcoming: [] }); }
      setBcastLoading(false);
    })();
  }, [open, step]);

  // STEP 3 · load short_clips only when video_to_live + short_clip.
  useEffect(() => {
    if (!open || step !== 3) return;
    if (creativeMode !== 'video_to_live' || creativeKind !== 'short_clip') return;
    (async () => {
      setClipsLoading(true);
      try {
        const r = await api.get('/seller/short-clips');
        setShortClips(r.data?.items || []);
      } catch { setShortClips([]); }
      setClipsLoading(false);
    })();
  }, [open, step, creativeMode, creativeKind]);

  // STEP 6 · wallet snapshot
  useEffect(() => {
    if (!open || step !== 6) return;
    (async () => {
      try { const r = await api.get('/seller/wallet'); setWallet(r.data); }
      catch { setWallet(null); }
    })();
  }, [open, step]);

  // When timing changes, reset the appropriate defaults for the wizard state.
  useEffect(() => {
    if (!selectedBroadcast) return;
    if (selectedBroadcast.is_live && !TIMING_LIVE_NOW.find(t => t.value === timingMode)) {
      setTimingMode('until_end');
    }
    if (!selectedBroadcast.is_live && !TIMING_UPCOMING.find(t => t.value === timingMode)) {
      setTimingMode('at_start');
    }
  }, [selectedBroadcast, timingMode]);

  if (!open) return null;

  // ── Derived state
  const hasAnyBroadcast = broadcasts.live_now.length + broadcasts.upcoming.length > 0;
  const isLiveNow = !!selectedBroadcast?.is_live;
  const timingOptions = isLiveNow ? TIMING_LIVE_NOW : TIMING_UPCOMING;

  const creative_source =
    creativeMode !== 'video_to_live' ? null :
    creativeKind === 'short_clip' && selectedClip
      ? { kind: 'short_clip', ref_id: selectedClip.clip_id, platform,
          video_url: selectedClip.video_url,
          thumbnail_url: selectedClip.thumbnail_url,
          embed_url: selectedClip.embed_url || null,
          source_url: selectedClip.source_url || null,
          media_type: 'video' }
      : creativeKind === 'upload' && uploaded
        ? uploaded
        : null;

  const finalLbc = {
    ...lbc,
    promo_type:       'viewer_boost',
    creative_mode:    creativeMode,
    live_timing_mode: timingMode,
    broadcast_id:     advancedOpen ? null : (selectedBroadcast?.schedule_id || null),
    broadcast_url:    advancedOpen ? broadcastUrl : null,
  };

  const lbcValid = !!(
    finalLbc.creative_mode &&
    (finalLbc.broadcast_id || (finalLbc.broadcast_url && /^https?:\/\//i.test(finalLbc.broadcast_url))) &&
    finalLbc.promo_message &&
    finalLbc.cta_label
  );

  const canNext = {
    1: true,
    2: !!(selectedBroadcast || (advancedOpen && broadcastUrl && /^https?:\/\//i.test(broadcastUrl))),
    3: creativeMode === 'live_direct' ? true : !!creative_source,
    4: !!platform,
    5: true,
    6: !!budget && (timingMode !== 'custom' || (!!startAt && !!endAt && new Date(endAt) > new Date(startAt))),
    7: true,
  }[step];

  // ── Upload handler (REUSE FAN BOOST pipeline)
  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isVideo && !isImage) { toast.error('영상 또는 이미지 파일을 선택해주세요.'); return; }
    setUploading(true);
    try {
      if (isVideo) {
        const fd = new FormData(); fd.append('file', file);
        const res = await api.post('/seller/short-clip-media', fd);
        const { media_id, url } = res.data || {};
        const full = url.startsWith('http') ? url : `${window.location.origin}${url}`;
        setUploaded({ kind: 'short_clip_media', ref_id: media_id, platform,
                      video_url: full, media_type: 'video' });
        toast.success('영상이 업로드되었습니다.');
      } else {
        if (file.size > 5 * 1024 * 1024) {
          toast.error('이미지는 5MB 이하로 업로드해주세요.'); setUploading(false); return;
        }
        const fd = new FormData(); fd.append('file', file); fd.append('kind', 'broadcast');
        const res = await api.post('/uploads', fd);
        setUploaded({ kind: 'upload', ref_id: res.data?.file_id, platform,
                      thumbnail_url: res.data?.url, media_type: 'image' });
        toast.success('이미지가 업로드되었습니다.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || '업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const goRegisterBroadcast = () => {
    onClose?.();
    window.location.assign('/seller/dashboard?section=live');
  };

  const save = async (submit = false) => {
    if (saving) return;
    setSaving(true);
    try {
      // Compute canonical start/end when timingMode != 'custom' — the server
      // still validates and stores whatever we send.  No external platform is
      // called; these are internal canonical values only.
      let s = startAt, e = endAt;
      if (timingMode !== 'custom') {
        const now = new Date();
        s = new Date(now.getTime() + 5 * 60_000).toISOString();
        e = computeSuggestedEnd(s, timingMode)?.toISOString()
            || new Date(now.getTime() + 24 * 3_600_000).toISOString();
      } else {
        s = new Date(startAt).toISOString();
        e = new Date(endAt).toISOString();
      }
      const created = await api.post('/seller/growth/live-boost', {
        title:  title || `${PLATFORMS.find(p=>p.value===platform)?.label} LIVE 부스트`,
        platform,
        creative_source,
        live_boost_config: finalLbc,
        targeting,
        budget_krw: Number(budget),
        start_at:   s,
        end_at:     e,
      });
      const ad_id = created.data?.campaign?.ad_id;
      if (!ad_id) throw new Error('생성 실패');
      if (submit) await api.post(`/seller/growth/live-boost/${ad_id}/submit`);
      toast.success(submit ? '승인 요청이 접수되었습니다.' : '초안이 저장되었습니다.');
      onCreated?.(); onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // ── UI helpers
  const StepHeader = ({ n, label }) => (
    <div className={`flex items-center gap-1.5 text-xs ${step >= n ? 'text-black dark:text-white font-bold' : 'text-gray-400'}`}>
      <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] ${step >= n ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}>{n}</span>
      <span>{label}</span>
    </div>
  );
  const stepLabels = ['안내','LIVE 선택','광고 방식','플랫폼','타깃','예산·시간','미리보기'];

  const budgetOK = Number(budget) >= 10000;
  const walletBalance = wallet ? Number(wallet.balance || 0) : null;
  const insufficient = walletBalance != null && walletBalance < Number(budget);

  const BroadcastCard = ({ b, live }) => (
    <label
      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${selectedBroadcast?.schedule_id===b.schedule_id ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10 hover:border-black/30'}`}
      data-testid={`liveboost-bcast-${b.schedule_id}`}>
      <input type="radio" name="bcast" checked={selectedBroadcast?.schedule_id===b.schedule_id}
             onChange={() => setSelectedBroadcast(b)} className="sr-only" />
      <div className="w-14 h-14 rounded-lg bg-black/10 dark:bg-white/10 overflow-hidden flex-shrink-0">
        {b.thumbnail ? <img src={b.thumbnail} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display='none'; }} />
                     : <div className="w-full h-full flex items-center justify-center text-gray-400"><Video size={16}/></div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-black ${live ? 'bg-red-500/10 text-red-600' : 'bg-blue-500/10 text-blue-600'}`}>
            {live ? '🔴 LIVE' : '🕒 예정'}
          </span>
          {b.platform && <span className="text-[10px] text-gray-400 uppercase">{b.platform}</span>}
          {b.category && <span className="text-[10px] text-gray-400">· {b.category}</span>}
        </div>
        <p className="mt-1 text-sm font-bold truncate">{b.title}</p>
        <p className="text-[11px] text-gray-500">{fmtDate(b.scheduled_at)}</p>
      </div>
      {selectedBroadcast?.schedule_id === b.schedule_id && <Check size={16} className="text-[#FF3B30] flex-shrink-0" />}
    </label>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="liveboost-wizard">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#141414] border border-black/10 dark:border-white/10 rounded-2xl">
        <div className="flex items-center justify-between p-4 border-b border-black/5 dark:border-white/10 sticky top-0 bg-white dark:bg-[#141414] z-10">
          <h3 className="text-lg font-black">LIVE 방송 부스트 만들기</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-black dark:hover:text-white" data-testid="liveboost-close"><X size={18} /></button>
        </div>

        <div className="p-3 flex flex-wrap gap-1.5 border-b border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5">
          {stepLabels.map((l, i) => (
            <React.Fragment key={i}>
              <StepHeader n={i + 1} label={l} />
              {i < stepLabels.length - 1 && <ChevronRight size={12} className="text-gray-300 self-center" />}
            </React.Fragment>
          ))}
        </div>

        <div className="p-5 min-h-[320px]">
          {/* ===================================================================== */}
          {/* STEP 1 · Intro                                                        */}
          {/* ===================================================================== */}
          {step === 1 && (
            <div className="max-w-md mx-auto text-center py-6" data-testid="liveboost-intro">
              <Sparkles size={36} className="mx-auto text-[#FF3B30] mb-3" />
              <h2 className="text-2xl font-black">LIVE 부스터</h2>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                방송 중이거나 예정된 LIVE를 홍보해<br/>더 많은 시청자를 유입하세요.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-3 text-left">
                <div className="p-3 rounded-xl border border-black/10 dark:border-white/10">
                  <div className="flex items-center gap-1.5 text-[#FF3B30] font-black text-xs mb-1">
                    <Radio size={14} /> 지금 방송 중
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">현재 LIVE로 시청자를 빠르게 유입시킵니다.</p>
                </div>
                <div className="p-3 rounded-xl border border-black/10 dark:border-white/10">
                  <div className="flex items-center gap-1.5 text-blue-600 font-black text-xs mb-1">
                    <Calendar size={14} /> 예정 LIVE
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">방송 시작 전부터 미리 광고를 준비합니다.</p>
                </div>
              </div>
              <p className="mt-5 text-[10px] text-gray-400 leading-relaxed">
                실제 광고 송출 기능은 연결된 광고 계정과 각 플랫폼에서 승인된 기능에 따라 제공됩니다.
              </p>
            </div>
          )}

          {/* ===================================================================== */}
          {/* STEP 2 · Broadcast picker                                             */}
          {/* ===================================================================== */}
          {step === 2 && (
            <div className="space-y-5" data-testid="liveboost-step-broadcast">
              <div>
                <p className="text-sm font-black mb-1">홍보할 LIVE 선택</p>
                <p className="text-xs text-gray-500">현재 방송 중이거나 예정된 LIVE를 선택해주세요.</p>
              </div>

              {bcastLoading ? (
                <p className="text-xs text-gray-400 py-8 text-center"><Loader2 size={14} className="inline animate-spin mr-1" /> 불러오는 중…</p>
              ) : !hasAnyBroadcast ? (
                <div className="rounded-2xl border border-dashed border-black/10 dark:border-white/10 p-8 text-center" data-testid="liveboost-empty-bcast">
                  <Video size={26} className="mx-auto text-gray-400 mb-2" />
                  <p className="text-sm font-bold">홍보할 예정/진행 LIVE가 없습니다.</p>
                  <p className="text-xs text-gray-500 mt-1">먼저 LIVE 방송을 등록해주세요.</p>
                  <button onClick={goRegisterBroadcast}
                    className="mt-4 inline-flex items-center gap-1 px-4 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-xs font-bold"
                    data-testid="liveboost-register-broadcast">
                    LIVE 방송 등록하기 <ArrowRight size={12} />
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {broadcasts.live_now.length > 0 && (
                    <div data-testid="liveboost-group-live">
                      <p className="text-xs font-black text-red-600 mb-2 flex items-center gap-1.5">
                        <Radio size={12} /> 지금 방송 중 ({broadcasts.live_now.length})
                      </p>
                      <div className="space-y-2">
                        {broadcasts.live_now.map((b) => <BroadcastCard key={b.schedule_id} b={b} live />)}
                      </div>
                    </div>
                  )}
                  {broadcasts.upcoming.length > 0 && (
                    <div data-testid="liveboost-group-upcoming">
                      <p className="text-xs font-black text-blue-600 mb-2 flex items-center gap-1.5">
                        <Calendar size={12} /> 예정 LIVE ({broadcasts.upcoming.length})
                      </p>
                      <div className="space-y-2">
                        {broadcasts.upcoming.map((b) => <BroadcastCard key={b.schedule_id} b={b} live={false} />)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Advanced · external URL fallback (rare · not the default flow) */}
              <details className="rounded-xl border border-black/5 dark:border-white/10 p-3"
                open={advancedOpen}
                onToggle={(e) => setAdvancedOpen(e.currentTarget.open)}
                data-testid="liveboost-advanced">
                <summary className="cursor-pointer text-xs font-bold text-gray-500 flex items-center gap-1.5">
                  <Info size={12} /> 고급 · LIVEMOA에 등록되지 않은 외부 LIVE 연결
                </summary>
                <div className="mt-3 space-y-2">
                  <p className="text-[11px] text-gray-500">
                    LIVEMOA에 예정 LIVE가 등록된 경우에는 이 옵션을 사용하지 마세요. 내 방송 선택이 canonical 흐름입니다.
                  </p>
                  <input value={broadcastUrl}
                    onChange={(e) => { setBroadcastUrl(e.target.value.slice(0, 500)); setSelectedBroadcast(null); }}
                    placeholder="https://shoppinglive.naver.com/lives/... · https://youtube.com/live/..."
                    className="w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                    data-testid="liveboost-bcast-url" />
                </div>
              </details>
            </div>
          )}

          {/* ===================================================================== */}
          {/* STEP 3 · Creative mode                                                */}
          {/* ===================================================================== */}
          {step === 3 && (
            <div className="space-y-5" data-testid="liveboost-step-creative">
              <div>
                <p className="text-sm font-black mb-1">어떻게 LIVE를 홍보할까요?</p>
                {selectedBroadcast && (
                  <p className="text-xs text-gray-500">
                    선택한 LIVE: <b>{selectedBroadcast.title}</b> · {selectedBroadcast.is_live ? '방송 중' : '예정'}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CREATIVE_MODES.map((m) => (
                  <button key={m.value} type="button" onClick={() => setCreativeMode(m.value)}
                    className={`text-left p-4 rounded-xl border-2 transition ${creativeMode===m.value ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/5 dark:border-white/10 hover:border-black/20'}`}
                    data-testid={`liveboost-cmode-${m.value}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <m.Icon size={20} className="text-[#FF3B30]" />
                      <p className="font-black">{m.label}</p>
                      {creativeMode===m.value && <Check size={16} className="text-[#FF3B30] ml-auto" />}
                    </div>
                    <p className="text-xs font-bold text-gray-600 dark:text-gray-300">{m.headline}</p>
                    <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{m.hint}</p>
                  </button>
                ))}
              </div>

              {/* Video creative panel appears ONLY for video_to_live. */}
              {creativeMode === 'video_to_live' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8 md:items-start pt-2 border-t border-black/5 dark:border-white/10" data-testid="liveboost-video-panel">
                  <div className="space-y-3 min-w-0">
                    <div className="flex gap-2">
                      <button onClick={() => setCreativeKind('short_clip')}
                        className={`flex-1 py-2 rounded-full text-sm font-bold ${creativeKind==='short_clip' ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}
                        data-testid="liveboost-creative-existing">
                        <Film size={14} className="inline mr-1" /> 기존 숏폼 선택
                      </button>
                      <button onClick={() => setCreativeKind('upload')}
                        className={`flex-1 py-2 rounded-full text-sm font-bold ${creativeKind==='upload' ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}
                        data-testid="liveboost-creative-upload">
                        <UploadIcon size={14} className="inline mr-1" /> 광고 소재 업로드
                      </button>
                    </div>
                    {creativeKind === 'short_clip' ? (
                      <>
                        <div className="flex flex-wrap gap-1.5" data-testid="liveboost-clip-platform-filter">
                          {PLATFORM_FILTERS.map((p) => (
                            <button key={p.value} type="button"
                              onClick={() => { setClipPlatformFilter(p.value); setSelectedClip(null); }}
                              className={`px-3 py-1 rounded-full text-xs font-bold ${clipPlatformFilter === p.value ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10 hover:bg-black/10'}`}>
                              {p.label}
                            </button>
                          ))}
                        </div>
                        <div className="max-h-[240px] overflow-y-auto space-y-2 mt-2">
                          {clipsLoading ? (
                            <p className="text-xs text-gray-400 py-6 text-center"><Loader2 size={14} className="inline animate-spin mr-1" /> 불러오는 중…</p>
                          ) : shortClips
                              .filter((c) => clipPlatformFilter === 'all' || (c.platform || '').toLowerCase() === clipPlatformFilter)
                              .length === 0 ? (
                            <p className="text-xs text-gray-400 py-6 text-center">등록된 숏폼이 없습니다.</p>
                          ) : shortClips
                              .filter((c) => clipPlatformFilter === 'all' || (c.platform || '').toLowerCase() === clipPlatformFilter)
                              .map((c) => (
                            <label key={c.clip_id}
                              className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer ${selectedClip?.clip_id===c.clip_id ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/5 dark:border-white/10'}`}
                              data-testid={`liveboost-clip-${c.clip_id}`}>
                              <input type="radio" name="clip-lb" checked={selectedClip?.clip_id===c.clip_id}
                                onChange={() => setSelectedClip(c)} className="sr-only" />
                              <div className="w-12 h-20 rounded-md bg-black/10 dark:bg-white/10 overflow-hidden flex-shrink-0">
                                {c.thumbnail_url ? <img src={c.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                                 : <div className="w-full h-full flex items-center justify-center text-gray-400"><Film size={16}/></div>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold truncate">{c.title || c.clip_id}</p>
                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 flex-wrap">
                                  <span className="uppercase font-bold">{c.platform || 'LIVEMOA'}</span>
                                  {c.duration_sec != null && <span>· {fmtDur(c.duration_sec)}</span>}
                                </div>
                              </div>
                              {selectedClip?.clip_id===c.clip_id && <Check size={16} className="text-[#FF3B30] flex-shrink-0" />}
                            </label>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div>
                        <input ref={fileRef} type="file"
                          accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp"
                          onChange={handleFileChosen} className="hidden"
                          data-testid="liveboost-upload-input" />
                        <button onClick={() => fileRef.current?.click()} disabled={uploading}
                          className="w-full aspect-[9/16] max-w-[220px] rounded-xl border-2 border-dashed border-black/10 dark:border-white/10 flex flex-col items-center justify-center hover:border-[#FF3B30] transition"
                          data-testid="liveboost-upload-btn">
                          {uploading
                            ? <><Loader2 size={22} className="animate-spin text-[#FF3B30] mb-2" /><span className="text-xs">업로드 중…</span></>
                            : <><UploadIcon size={22} className="text-gray-400 mb-2" />
                                <span className="text-xs font-bold">영상 또는 이미지 업로드</span>
                                <span className="text-[10px] text-gray-400 mt-1">MP4/MOV/WEBM · JPG/PNG/WEBP</span></>}
                        </button>
                        {uploaded && (
                          <p className="text-xs text-emerald-600 mt-2"><Check size={12} className="inline" /> {uploaded.media_type === 'video' ? '영상' : '이미지'} 업로드 완료</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-center gap-2 md:pt-0 pt-2">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">9:16 미리보기</p>
                    <CreativePreview
                      media_type={creative_source?.media_type}
                      video_url={creative_source?.video_url}
                      thumbnail_url={creative_source?.thumbnail_url}
                      embed_url={creative_source?.embed_url}
                      source_url={creative_source?.source_url}
                    />
                  </div>
                </div>
              )}

              {/* Promo message + CTA (both modes) */}
              <div className="rounded-xl border border-black/5 dark:border-white/10 p-4 space-y-3" data-testid="liveboost-lbc-panel">
                <p className="text-sm font-black">광고 문구</p>
                <label className="block text-sm">
                  <span className="font-bold">① 광고 홍보 문구</span>
                  <textarea rows={2} value={lbc.promo_message || ''}
                    onChange={(e) => setLbc((s) => ({ ...s, promo_message: e.target.value.slice(0, 200) }))}
                    className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                    placeholder={LBC_DEFAULTS.promo_message}
                    data-testid="liveboost-lbc-promo" />
                </label>
                <label className="block text-sm">
                  <span className="font-bold">② CTA 버튼 문구</span>
                  <input value={lbc.cta_label || ''}
                    onChange={(e) => setLbc((s) => ({ ...s, cta_label: e.target.value.slice(0, 30) }))}
                    className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10 text-sm"
                    placeholder={LBC_DEFAULTS.cta_label}
                    data-testid="liveboost-lbc-cta" />
                </label>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* STEP 4 · Ad platform                                                  */}
          {/* ===================================================================== */}
          {step === 4 && (
            <div className="space-y-3" data-testid="liveboost-step-platform">
              <p className="text-sm font-black">광고 플랫폼</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {PLATFORMS.map((p) => (
                  <button key={p.value} onClick={() => setPlatform(p.value)}
                    className={`p-4 rounded-xl border-2 transition ${platform===p.value ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/5 dark:border-white/10 hover:border-black/20'}`}
                    data-testid={`liveboost-platform-${p.value}`}>
                    <p.Icon size={20} className="mb-2 mx-auto text-[#FF3B30]" />
                    <p className="font-black">{p.label}</p>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                실제 광고 송출 기능은 연결된 광고 계정과 플랫폼에서 승인된 기능에 따라 제공됩니다.
                현재 단계에서는 LIVEMOA 내부 canonical 설정만 저장됩니다.
              </p>
            </div>
          )}

          {/* ===================================================================== */}
          {/* STEP 5 · Targeting                                                    */}
          {/* ===================================================================== */}
          {step === 5 && (
            <div className="space-y-4 text-sm" data-testid="liveboost-target-panel">
              <div>
                <p className="font-bold mb-2">지역</p>
                <div className="flex flex-wrap gap-1.5">
                  {REGIONS.map((r) => {
                    const isNationwide = r === NATIONWIDE;
                    const active = targeting.regions.includes(r);
                    return (
                      <button key={r} type="button"
                        onClick={() => setTargeting((t) => {
                          if (isNationwide) return { ...t, regions: active ? [] : [NATIONWIDE] };
                          const wo = t.regions.filter((x) => x !== NATIONWIDE);
                          return { ...t, regions: wo.includes(r) ? wo.filter((x) => x !== r) : [...wo, r] };
                        })}
                        className={`px-3 py-1 rounded-full text-xs font-bold ${active ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'} ${isNationwide ? 'ring-1 ring-[#FF3B30]/30' : ''}`}
                        data-testid={isNationwide ? 'liveboost-region-nationwide' : `liveboost-region-${r}`}>{r}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="font-bold mb-2">연령</p>
                <div className="flex items-center gap-2">
                  <input type="number" min="0" max="100" value={targeting.age_min}
                    onChange={(e) => setTargeting((t)=>({ ...t, age_min: Number(e.target.value)||0 }))}
                    className="w-20 px-2 py-1 border rounded text-sm bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" />
                  <span>~</span>
                  <input type="number" min="0" max="100" value={targeting.age_max}
                    onChange={(e) => setTargeting((t)=>({ ...t, age_max: Number(e.target.value)||0 }))}
                    className="w-20 px-2 py-1 border rounded text-sm bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10" />
                  <span className="text-xs text-gray-400">세</span>
                </div>
              </div>
              <div>
                <p className="font-bold mb-2">성별</p>
                <div className="flex gap-2">
                  {GENDERS.map((g) => (
                    <button key={g.v} onClick={() => setTargeting((t)=>({ ...t, genders: [g.v] }))}
                      className={`px-3 py-1 rounded-full text-xs font-bold ${targeting.genders.includes(g.v) ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}>
                      {g.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-bold mb-2">관심 카테고리</p>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map((c) => (
                    <button key={c} type="button"
                      onClick={() => setTargeting((t) => ({ ...t, categories: t.categories.includes(c) ? t.categories.filter(x=>x!==c) : [...t.categories, c] }))}
                      className={`px-3 py-1 rounded-full text-xs font-bold ${targeting.categories.includes(c) ? 'bg-[#FF3B30] text-white' : 'bg-black/5 dark:bg-white/10'}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* STEP 6 · Budget + timing                                              */}
          {/* ===================================================================== */}
          {step === 6 && (
            <div className="space-y-4 text-sm" data-testid="liveboost-budget-panel">
              <label className="block">
                <span className="font-bold">캠페인 명 (선택)</span>
                <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 120))}
                  placeholder={isLiveNow ? '지금 LIVE 부스트' : '예정 LIVE 부스트'}
                  className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
                  data-testid="liveboost-title" />
              </label>

              <div>
                <p className="font-bold mb-2">{isLiveNow ? '광고 시간' : '광고 시작'}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="liveboost-timing-modes">
                  {timingOptions.map((t) => (
                    <button key={t.value} onClick={() => setTimingMode(t.value)}
                      className={`p-2 rounded-lg text-left border transition ${timingMode===t.value ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/5 dark:border-white/10'}`}
                      data-testid={`liveboost-timing-${t.value}`}>
                      <p className="text-sm font-bold">{t.label}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{t.hint}</p>
                    </button>
                  ))}
                </div>
                {timingMode === 'custom' && (
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <label className="block">
                      <span className="font-bold text-xs">시작</span>
                      <input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
                        data-testid="liveboost-start" />
                    </label>
                    <label className="block">
                      <span className="font-bold text-xs">종료</span>
                      <input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
                        data-testid="liveboost-end" />
                    </label>
                  </div>
                )}
                <p className="mt-2 text-[10px] text-gray-400 leading-relaxed">
                  선택한 옵션은 LIVEMOA 내부 canonical 설정으로 저장됩니다. 실제 광고 플랫폼별 스케줄링 지원 여부는 외부 API 연결 단계에서 매핑됩니다.
                </p>
              </div>

              <label className="block">
                <span className="font-bold">총 예산 (원)</span>
                <input type="number" min="10000" step="10000" value={budget}
                  onChange={(e) => setBudget(Number(e.target.value)||0)}
                  className="mt-1 w-full px-3 py-2 border rounded bg-white dark:bg-[#0A0A0A] border-black/10 dark:border-white/10"
                  data-testid="liveboost-budget" />
                <span className="text-[11px] text-gray-400">최소 10,000원 · 실제 결제는 승인 이후 별도 단계에서 진행됩니다.</span>
                {!budgetOK && <p className="text-[11px] text-red-500 mt-0.5"><AlertCircle size={11} className="inline" /> 최소 예산은 10,000원입니다.</p>}
              </label>

              <div className="p-3 rounded-xl bg-black/5 dark:bg-white/5" data-testid="liveboost-wallet">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold flex items-center gap-1.5"><Wallet size={14} /> 현재 잔액</span>
                  <span className="font-black">{wallet ? won(walletBalance) : '조회 중…'}</span>
                </div>
                <div className="flex items-center justify-between text-xs mt-1">
                  <span>예상 광고비</span>
                  <span className="font-bold">{won(Number(budget) || 0)}</span>
                </div>
                {insufficient && (
                  <p className="mt-2 text-[11px] text-red-500 flex items-center gap-1"><AlertCircle size={12} /> 잔액이 부족합니다. 승인 후 결제 단계에서 충전이 필요합니다.</p>
                )}
                <p className="mt-1 text-[10px] text-gray-400">이번 단계에서는 실제 지갑 차감이 발생하지 않습니다.</p>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* STEP 7 · Preview                                                      */}
          {/* ===================================================================== */}
          {step === 7 && (
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4" data-testid="liveboost-preview">
              <div className="flex flex-col items-center gap-2">
                {creativeMode === 'video_to_live' ? (
                  <>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">9:16 미리보기</p>
                    <CreativePreview
                      media_type={creative_source?.media_type}
                      video_url={creative_source?.video_url}
                      thumbnail_url={creative_source?.thumbnail_url}
                      embed_url={creative_source?.embed_url}
                      source_url={creative_source?.source_url}
                    />
                  </>
                ) : (
                  <div className="w-[220px] aspect-[9/16] rounded-xl bg-gradient-to-br from-red-500 to-pink-600 text-white flex flex-col items-center justify-center p-4 text-center">
                    <Radio size={32} className="mb-2" />
                    <p className="text-xs font-black">LIVE 바로 홍보</p>
                    <p className="mt-1 text-[10px] leading-tight opacity-90 line-clamp-4">{lbc.promo_message}</p>
                    <div className="mt-4 px-3 py-1.5 rounded-full bg-white/20 text-[10px] font-black">{lbc.cta_label}</div>
                  </div>
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="p-3 rounded-lg bg-black/5 dark:bg-white/5 space-y-1">
                  <p className="font-black text-xs text-[#FF3B30] mb-1">LIVE 부스터</p>
                  <p><b>선택한 LIVE:</b> {selectedBroadcast?.title || (advancedOpen ? '외부 LIVE URL' : '-')}</p>
                  <p><b>상태:</b> {selectedBroadcast?.is_live ? '🔴 방송 중' : selectedBroadcast ? '🕒 예정' : '-'}</p>
                  {selectedBroadcast?.platform && <p><b>방송 플랫폼:</b> {selectedBroadcast.platform}</p>}
                  <p><b>광고 플랫폼:</b> {PLATFORMS.find(p=>p.value===platform)?.label}</p>
                  <p><b>광고 방식:</b> {CREATIVE_MODES.find(m=>m.value===creativeMode)?.label}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#FF3B30]/5 border border-[#FF3B30]/20 space-y-1" data-testid="liveboost-preview-lbc">
                  <p><b>광고 문구:</b> <span className="inline">{lbc.promo_message || '-'}</span></p>
                  <p><b>CTA:</b> {lbc.cta_label || '-'}</p>
                </div>
                <div className="p-3 rounded-lg bg-black/5 dark:bg-white/5 space-y-1">
                  <p><b>지역:</b> {targeting.regions.join(', ') || '전체'}</p>
                  <p><b>연령:</b> {targeting.age_min} ~ {targeting.age_max}세</p>
                  <p><b>성별:</b> {GENDERS.find(g=>targeting.genders.includes(g.v))?.l || '전체'}</p>
                  <p><b>카테고리:</b> {targeting.categories.join(', ') || '전체'}</p>
                  <p><b>예산:</b> {won(budget)}</p>
                  <p><b>{isLiveNow ? '광고 시간' : '광고 시작'}:</b> {timingOptions.find(t=>t.value===timingMode)?.label}
                    {timingMode === 'custom' && startAt && endAt && ` · ${fmtDate(startAt)} ~ ${fmtDate(endAt)}`}
                  </p>
                </div>
                <div className="p-3 rounded-lg border border-dashed border-black/10 dark:border-white/10 text-xs text-gray-500">
                  승인 요청 후 관리자 검토를 거쳐 승인되면 <b>APPROVED</b> 상태가 됩니다.
                  이번 버전에서는 외부 광고 REAL 송출은 이루어지지 않고, LIVEMOA LIVE 랜딩 URL이 활성화됩니다.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-black/5 dark:border-white/10 sticky bottom-0 bg-white dark:bg-[#141414]">
          <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-sm bg-black/5 dark:bg-white/10 disabled:opacity-40"
            data-testid="liveboost-prev"><ChevronLeft size={14} /> 이전</button>
          {step < 7 ? (
            <button onClick={() => canNext && setStep(step + 1)} disabled={!canNext}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold disabled:opacity-40"
              data-testid="liveboost-next">다음 <ChevronRight size={14} /></button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => save(false)} disabled={saving}
                className="px-3 py-2 rounded-full text-sm bg-black/5 dark:bg-white/10 font-bold"
                data-testid="liveboost-save-draft">초안 저장</button>
              <button onClick={() => save(true)} disabled={saving}
                className="px-4 py-2 rounded-full text-sm bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold"
                data-testid="liveboost-submit">승인 요청</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Section
// ═════════════════════════════════════════════════════════════════════════
export default function LiveBoostSection({ sellerId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/seller/growth/live-boost'); setItems(r.data || []); }
    catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (ad_id) => {
    try {
      await api.post(`/seller/growth/live-boost/${ad_id}/submit`);
      toast.success('승인 요청이 접수되었습니다.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
  };

  const copyLanding = async (ad_id) => {
    try { await navigator.clipboard.writeText(buildLandingUrl(ad_id)); toast.success('링크가 복사되었습니다.'); }
    catch { toast.error('복사에 실패했습니다.'); }
  };

  return (
    <div className="mt-6 space-y-3" data-testid="liveboost-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-black">LIVE 방송 부스트</h3>
          <p className="text-xs text-gray-500 mt-0.5">현재 방송 중이거나 예정된 LIVE로 시청자를 유입합니다.</p>
        </div>
        <button onClick={() => setWizardOpen(true)}
          className="px-3 py-2 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold"
          data-testid="liveboost-new">
          새 캠페인 만들기
        </button>
      </div>

      <LiveBoostWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onCreated={load} />

      {loading ? (
        <p className="text-xs text-gray-400 py-6 text-center">불러오는 중…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-400 py-6 text-center" data-testid="liveboost-empty">등록된 LIVE 부스트 캠페인이 없습니다.</p>
      ) : (
        <div className="divide-y divide-black/5 dark:divide-white/10 bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-xl" data-testid="liveboost-list">
          {items.map((c) => {
            const cmode = c.live_boost_config?.creative_mode;
            return (
              <div key={c.ad_id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid={`liveboost-item-${c.ad_id}`}>
                {c.creative_source?.thumbnail_url && (
                  <img src={c.creative_source.thumbnail_url} alt="" className="w-10 h-16 rounded object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${STATUS_TONE[c.status] || 'bg-black/5'}`}>
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-black/5 dark:bg-white/10 uppercase">{c.platform || '-'}</span>
                    {cmode && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-black/5 dark:bg-white/10">{cmode === 'live_direct' ? 'LIVE 바로 홍보' : '영상형'}</span>}
                  </div>
                  <p className="mt-1 text-sm font-bold truncate">{c.title || 'LIVE 부스트'}</p>
                  <p className="text-[11px] text-gray-500">
                    {won(c.budget_krw)} · {c.start_at ? new Date(c.start_at).toLocaleDateString('ko-KR') : '-'} ~ {c.end_at ? new Date(c.end_at).toLocaleDateString('ko-KR') : '-'}
                  </p>
                  {c.rejection_reason && <p className="text-[11px] text-red-500 mt-0.5">반려 사유: {c.rejection_reason}</p>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {c.status === 'draft' && (
                    <button onClick={() => submit(c.ad_id)}
                      className="px-2.5 py-1 rounded-full text-xs bg-[#FF3B30] text-white font-bold"
                      data-testid={`liveboost-submit-${c.ad_id}`}>승인 요청</button>
                  )}
                  {(c.status === 'approved' || c.status === 'running') && (
                    <>
                      <a href={buildLandingUrl(c.ad_id)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-black/5 dark:bg-white/10 font-bold"
                        data-testid={`liveboost-open-${c.ad_id}`}>
                        <ExternalLink size={12} /> 열기
                      </a>
                      <button onClick={() => copyLanding(c.ad_id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-black/5 dark:bg-white/10 font-bold"
                        data-testid={`liveboost-copy-${c.ad_id}`}>
                        <Copy size={12} /> 링크 복사
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
